'use client';

import React from 'react';
import type { OrderItem } from '@/lib/store';

interface OrderSummarySectionProps {
  items: OrderItem[];
  onRemove: (productId: string) => void;
}

export default function OrderSummarySection({ items, onRemove }: OrderSummarySectionProps) {
  const total = items.reduce((sum, item) => sum + item.lineTotal, 0);

  return (
    <section className="px-4 py-6 max-w-sm mx-auto sm:max-w-2xl">
      <div
        className="rounded-xl p-4 border"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        <div className="divider-ornament mb-4">
          <span
            className="text-xs font-semibold tracking-widest uppercase"
            style={{ color: 'var(--secondary)' }}
          >
            Your Selection
          </span>
        </div>

        <div className="flex flex-col gap-3">
          {items.map((item) => (
            <div
              key={`summary-${item.productId}-${item.sizeId}`}
              className="flex items-center justify-between gap-2"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate" style={{ color: 'var(--foreground)' }}>
                  {item.productName}
                </p>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                  {item.sizeLabel} × {item.quantity}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span
                  className="text-sm font-bold tabular-nums"
                  style={{ color: 'var(--primary)' }}
                >
                  ₹{item.lineTotal}
                </span>
                <button
                  onClick={() => onRemove(item.productId)}
                  className="text-xs"
                  style={{ color: 'var(--muted-foreground)' }}
                  aria-label={`Remove ${item.productName}`}
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>

        <div
          className="mt-4 pt-3 flex items-center justify-between"
          style={{ borderTop: '1px solid var(--border)' }}
        >
          <span className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>
            Total
          </span>
          <span
            className="text-lg font-bold font-serif tabular-nums"
            style={{ color: 'var(--primary)' }}
          >
            ₹{total}
          </span>
        </div>

        <p className="mt-3 text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
          This is a pre-order — no payment required now. We&apos;ll confirm your final order and payment details on WhatsApp.
        </p>
      </div>
    </section>
  );
}